/**
 * WorkspacePicker — modal surface for switching the active workspace.
 *
 * Mirrors the DoctypePicker / LibraryHub pattern: a Fluent Dialog with
 * a single canonical list. Replaces the "click workspace chip → start
 * draft" misdirection that was wired before — the chip now does what
 * writers expect (open a picker so they can SWITCH workspaces).
 *
 * Used by:
 *   - ContextStrip "workspace" chip click in VariantCShell
 *
 * SRP: pure presentation + selection. The CALLER decides what to do
 * with the new id (typically ``handleSelectedWorkspaceIdChange`` which
 * persists + cascades library refetch).
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { Workspace } from "../../types";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export interface WorkspacePickerProps {
  open: boolean;
  workspaces: Workspace[];
  selectedWorkspaceId: string | null;
  onSelect: (workspaceId: string) => void;
  onCancel: () => void;
  /** Create a custom (billable) workspace, then select it. When absent, the
   *  "+ New workspace" affordance is hidden. */
  onCreate?: (name: string) => Promise<void>;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: "480px",
    width: "100%",
    backgroundColor: surfaceColors.paper,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingTop: "8px",
  },
  empty: {
    fontFamily: fonts.body,
    color: inkColors.ink3,
    fontSize: "14px",
    padding: "12px 0",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paper,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "14px",
    color: inkColors.ink,
    textAlign: "left",
    width: "100%",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
      borderTopColor: brandColors.primary,
      borderRightColor: brandColors.primary,
      borderBottomColor: brandColors.primary,
      borderLeftColor: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  rowActive: {
    borderTopColor: brandColors.primary,
    borderRightColor: brandColors.primary,
    borderBottomColor: brandColors.primary,
    borderLeftColor: brandColors.primary,
    backgroundColor: brandColors.light,
  },
  name: {
    fontWeight: 500,
  },
  meta: {
    fontSize: "12px",
    color: inkColors.ink3,
  },
  createRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    paddingTop: "8px",
    marginTop: "4px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  createHint: {
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink3,
    paddingTop: "2px",
  },
});

export const WorkspacePicker: React.FC<WorkspacePickerProps> = ({
  open,
  workspaces,
  selectedWorkspaceId,
  onSelect,
  onCancel,
  onCreate,
}) => {
  const styles = useStyles();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleSelect = (id: string): void => {
    if (id === selectedWorkspaceId) {
      // Same workspace — just close.
      onCancel();
      return;
    }
    onSelect(id);
  };

  const submitCreate = async (): Promise<void> => {
    const name = newName.trim();
    if (!name || !onCreate) return;
    setBusy(true);
    setCreateError(null);
    try {
      await onCreate(name);
      setCreating(false);
      setNewName("");
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Could not create the workspace.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Pick a workspace</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              {workspaces.length === 0 && <Text className={styles.empty}>No workspaces available.</Text>}
              {workspaces.length === 1 && (
                <Text className={styles.empty}>You only have one workspace — nothing to switch to.</Text>
              )}
              {workspaces.map((ws) => {
                const active = ws.id === selectedWorkspaceId;
                return (
                  <button
                    key={ws.id}
                    type="button"
                    onClick={() => handleSelect(ws.id)}
                    className={mergeClasses(styles.row, active && styles.rowActive)}
                    aria-current={active ? "true" : undefined}
                  >
                    <span className={styles.name}>{ws.name}</span>
                    <span className={styles.meta}>{active ? "active" : ws.is_admin ? "admin" : "member"}</span>
                  </button>
                );
              })}
              {onCreate &&
                (creating ? (
                  <form
                    className={styles.createRow}
                    onSubmit={(e) => {
                      e.preventDefault();
                      void submitCreate();
                    }}
                  >
                    <Input
                      value={newName}
                      onChange={(_, data) => setNewName(data.value)}
                      placeholder="New workspace name"
                      aria-label="New workspace name"
                      maxLength={120}
                      disabled={busy}
                      style={{ flex: 1 }}
                    />
                    <Button appearance="primary" type="submit" disabled={busy || newName.trim().length === 0}>
                      {busy ? <Spinner size="tiny" /> : "Create"}
                    </Button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className={styles.row}
                    onClick={() => setCreating(true)}
                  >
                    <span className={styles.name}>＋ New workspace</span>
                    <span className={styles.meta}>custom · billable</span>
                  </button>
                ))}
              {createError !== null && <Text className={styles.createHint}>{createError}</Text>}
              {onCreate && !creating && (
                <Text className={styles.createHint}>
                  Create a custom workspace for your own libraries. The demo workspace and its sample
                  libraries stay free.
                </Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default WorkspacePicker;
